/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import { ExtraActionsProps } from 'app/components/organisms/crud/form-types';
import { Modal } from 'app/components/organisms/modal/modal';
import { SectionHeader } from 'app/components/molecules/section-header/section-header';
import { cn } from 'app/lib/utils';
import { useBankAccounts } from 'app/hooks/requests/bank-accounts/use-bank-accounts';
import { useRefreshBankAccount } from 'app/hooks/requests/bank-accounts/refresh-bank-account';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { appRoutes } from 'app/routes';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from 'app/core/utils/formatter';
import BigNumber from 'bignumber.js';
import { Alert, AlertDescription, AlertTitle } from 'app/components/ui/alert';
import { AlertCircle, CheckCircle2, Clock, Landmark, Loader2, RefreshCw } from 'lucide-react';

/**
 * `ConnectionState` type.
 */

type ConnectionState = 'QUEUED' | 'UPDATED' | 'AUTHENTICATING' | 'REFRESHING' | 'ERROR' | 'TEMPORARY_ERROR';

/**
 * `Value` type.
 */

type Value = {
  state: ConnectionState;
  metadata?: any;
};

/**
 * `State` type.
 */

type State = {
  id: number;
  title: string;
  value: Value;
};

/**
 * `StatusBadge` component — the per-account refresh state indicator.
 */

function StatusBadge({
  tone,
  icon,
  label
}: {
  tone: 'success' | 'active' | 'muted' | 'error';
  icon: ReactNode;
  label: string;
}): JSX.Element {
  const toneClass = {
    success: 'text-primary',
    active: 'text-foreground',
    muted: 'text-muted-foreground',
    error: 'text-destructive'
  }[tone];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium [&_svg]:h-4 [&_svg]:w-4',
        toneClass
      )}
    >
      {icon}

      {label}
    </span>
  );
}

/**
 * Export `ExtraActions` component.
 */

export function ExtraActions({
  refresh,
  open,
  onOpenChange,
  hideTrigger
}: ExtraActionsProps & {
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  hideTrigger?: boolean;
}): JSX.Element {
  const { data } = useBankAccounts();
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  // Controlled when `open`/`onOpenChange` are provided (e.g. the dashboard card),
  // otherwise self-managed via the default trigger button.
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const bankAccounts = useMemo(() => {
    return data?.results?.filter(item => !!item.hasConnected) ?? [];
  }, [data]);

  const { mutateAsync: refreshBankAccount } = useRefreshBankAccount('refresh');
  const handleClick = useCallback(async () => {
    setOpen(true);
  }, [setOpen]);

  const handleReconnect = useCallback(
    (data: any) => {
      const route = resolveAppPath(appRoutes.dashboard.bankAccounts.accounts.connect, { id: data.id });

      navigate(route);
    },
    [navigate]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    async function refreshBankAccounts() {
      setLoading(true);
      const data = [];

      for (const bankAccount of bankAccounts) {
        data.push({
          id: bankAccount.id,
          title: bankAccount.name,
          value: {
            state: 'QUEUED'
          }
        } as State);
      }

      setState(data);
      let hasError = false;

      for (const item of data) {
        item.value = {
          state: 'REFRESHING'
        };

        setState(data);

        try {
          const result = await refreshBankAccount(item.id);

          if (result.status === 'UPDATED') {
            item.value.state = 'UPDATED';
            item.value.metadata = result;
          } else {
            hasError = true;
            item.value.state = 'ERROR';
            item.value.metadata = {
              ...result,
              bankAccountId: item.id
            };
          }
        } catch (error: any) {
          hasError = true;
          item.value.state = 'ERROR';
          item.value.metadata = error.response?.data?.data?.data;
        } finally {
          setState(data);
        }
      }

      setLoading(false);

      if (hasError) {
        return;
      }

      await refresh();

      setOpen(false);
    }

    refreshBankAccounts();
  }, [bankAccounts, isOpen, refresh, refreshBankAccount, setOpen, translate]);

  const resolveStatus = useCallback(
    (item: State): { status: ReactNode; note?: ReactNode } => {
      switch (item.value.state) {
        case 'ERROR': {
          const meta = item.value.metadata;

          if (meta?.error === 'invalid_import_balances') {
            return {
              status: (
                <StatusBadge
                  icon={<AlertCircle />}
                  label={translate('updateAccounts.labels.invalidBalances')}
                  tone={'error'}
                />
              ),
              note: (
                <Alert variant={'destructive'}>
                  <AlertCircle className={'h-4 w-4'} />

                  <AlertTitle>{translate('updateAccounts.labels.invalidBalances')}</AlertTitle>

                  <AlertDescription>
                    {translate('updateAccounts.labels.invalidBalancesDescription', {
                      networkBalance: formatCurrency(meta?.networkBalance),
                      appBalance: formatCurrency(meta?.appBalance),
                      diff: formatCurrency(new BigNumber(meta?.networkBalance).minus(meta?.appBalance).toFixed(2))
                    })}
                  </AlertDescription>
                </Alert>
              )
            };
          }

          if (!meta) {
            return {
              status: (
                <div className={'flex items-center gap-2'}>
                  <StatusBadge
                    icon={<AlertCircle />}
                    label={translate('updateAccounts.labels.internalError')}
                    tone={'error'}
                  />

                  <Button danger onClick={() => handleReconnect(item)} size={'small'}>
                    {translate('common.actions.goToBankConfig')}
                  </Button>
                </div>
              )
            };
          }

          return {
            status: (
              <Button danger href={meta?.updateConsentLink} size={'small'}>
                {translate('common.actions.reconnect')}
              </Button>
            )
          };
        }

        case 'UPDATED':
          return {
            status: (
              <StatusBadge icon={<CheckCircle2 />} label={translate('updateAccounts.labels.done')} tone={'success'} />
            )
          };
        case 'REFRESHING':
          return {
            status: (
              <StatusBadge
                icon={<Loader2 className={'animate-spin'} />}
                label={translate('updateAccounts.labels.refreshing')}
                tone={'active'}
              />
            )
          };
        default:
          return {
            status: <StatusBadge icon={<Clock />} label={translate('updateAccounts.labels.queued')} tone={'muted'} />
          };
      }
    },
    [handleReconnect, translate]
  );

  const updatedCount = state.filter(item => item.value.state === 'UPDATED').length;

  return (
    <>
      {!hideTrigger && (
        <Button isLoading={isOpen} onClick={handleClick}>
          {translate('common.actions.refresh')}
        </Button>
      )}

      <Modal
        buttons={[]}
        isLoading={isLoading}
        isOpen={isOpen}
        maskClosable={!isLoading}
        onClose={() => setOpen(false)}
      >
        <div>
          <SectionHeader
            description={isLoading ? translate('updateAccounts.labels.refreshing') : `${updatedCount}/${state.length}`}
            icon={<RefreshCw className={cn('h-5 w-5', isLoading && 'animate-spin')} />}
            title={translate('updateAccounts.title')}
          />

          {state.length > 0 && (
            <div className={'mt-4 h-1.5 overflow-hidden rounded-full bg-muted'}>
              <div
                className={'h-full rounded-full bg-primary transition-all duration-300'}
                style={{ width: `${(updatedCount / state.length) * 100}%` }}
              />
            </div>
          )}

          <div className={'mt-4 space-y-2'}>
            {state.map(item => {
              const { status, note } = resolveStatus(item);

              return (
                <div key={item.id}>
                  <div
                    className={'flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5'}
                  >
                    <div className={'flex min-w-0 items-center gap-2.5'}>
                      <span
                        className={'grid h-8 w-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground'}
                      >
                        <Landmark className={'h-4 w-4'} />
                      </span>

                      <span className={'truncate text-sm font-medium text-foreground'}>{item.title}</span>
                    </div>

                    {status}
                  </div>

                  {!!note && <div className={'mt-2'}>{note}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
}
